import React, { ReactNode, useMemo, useRef } from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import ColumnResizeHandle from './column-resize-handle.component';
import { useColumnWidths } from './use-column-widths';
import { ColumnDefinition, FrameworkCellSpec } from './types';

export type ResizableProfileTableProps<TItem, TKey extends string> = {
    profileId: string;
    initialWidths: Partial<Record<TKey, number>>;
    onPersistWidths: (widths: Record<string, number>) => void;
    persistDelayMs?: number;
    columns: Record<TKey, ColumnDefinition<TKey>>;
    activeColumns: TKey[];
    items: TItem[];
    getItemKey: (item: TItem) => string;
    renderHeaderLabel: (key: TKey) => ReactNode;
    renderRow: (props: { item: TItem; itemKey: string; activeColumns: TKey[]; index: number }) => ReactNode;
    leadingFrameworkCells?: FrameworkCellSpec[];
    trailingFrameworkCells?: FrameworkCellSpec[];
    minColumnWidth?: number;
    maxColumnWidth?: number;
    tableSize?: 'small' | 'medium';
    tableSx?: SxProps<Theme>;
};

const ResizableProfileTable = <TItem, TKey extends string>({
    profileId,
    initialWidths,
    onPersistWidths,
    persistDelayMs,
    columns,
    activeColumns,
    items,
    getItemKey,
    renderHeaderLabel,
    renderRow,
    leadingFrameworkCells = [],
    trailingFrameworkCells = [],
    minColumnWidth = 60,
    maxColumnWidth = 800,
    tableSize = 'medium',
    tableSx,
}: ResizableProfileTableProps<TItem, TKey>) => {
    const { getWidth, commitWidth } = useColumnWidths<TKey>({
        cacheKey: profileId,
        initialWidths,
        onPersist: onPersistWidths,
        persistDelayMs,
        columns,
        minWidth: minColumnWidth,
        maxWidth: maxColumnWidth,
    });

    const colRefs = useRef<Record<string, HTMLTableColElement | null>>({});

    const totalDeclaredWidth = useMemo(() => {
        let sum = 0;
        for (const cell of leadingFrameworkCells) sum += cell.width;
        for (const key of activeColumns) sum += getWidth(key);
        for (const cell of trailingFrameworkCells) sum += cell.width;
        return sum;
    }, [leadingFrameworkCells, trailingFrameworkCells, activeColumns, getWidth]);

    return (
        <Table size={tableSize} sx={{ tableLayout: 'fixed', minWidth: `${totalDeclaredWidth}px`, ...tableSx }}>
            <colgroup>
                {leadingFrameworkCells.map((cell, index) => (
                    <col key={`leading-${index}`} style={{ width: `${cell.width}px` }} />
                ))}
                {activeColumns.map((columnKey) => (
                    <col
                        key={columnKey}
                        ref={(el) => {
                            colRefs.current[columnKey] = el;
                        }}
                        style={{ width: `${getWidth(columnKey)}px` }}
                    />
                ))}
                <col />
                {trailingFrameworkCells.map((cell, index) => (
                    <col key={`trailing-${index}`} style={{ width: `${cell.width}px` }} />
                ))}
            </colgroup>
            <TableHead>
                <TableRow>
                    {leadingFrameworkCells.map((cell, index) =>
                        cell.renderHeaderCell ? (
                            <React.Fragment key={`leading-${index}`}>{cell.renderHeaderCell()}</React.Fragment>
                        ) : (
                            <TableCell key={`leading-${index}`} />
                        ),
                    )}
                    {activeColumns.map((columnKey) => {
                        const definition = columns[columnKey];
                        const colMin = definition?.minWidth ?? minColumnWidth;
                        const colMax = definition?.maxWidth ?? maxColumnWidth;
                        return (
                            <TableCell key={columnKey} sx={{ position: 'relative' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        maxWidth: '100%',
                                        minWidth: 0,
                                    }}
                                >
                                    {renderHeaderLabel(columnKey)}
                                </Box>
                                <ColumnResizeHandle
                                    initialWidth={getWidth(columnKey)}
                                    minWidth={colMin}
                                    maxWidth={colMax}
                                    onResize={(width) => {
                                        const colEl = colRefs.current[columnKey];
                                        if (colEl) colEl.style.width = `${width}px`;
                                    }}
                                    onResizeEnd={(width) => commitWidth(columnKey, width)}
                                />
                            </TableCell>
                        );
                    })}
                    <TableCell />
                    {trailingFrameworkCells.map((cell, index) =>
                        cell.renderHeaderCell ? (
                            <React.Fragment key={`trailing-${index}`}>{cell.renderHeaderCell()}</React.Fragment>
                        ) : (
                            <TableCell key={`trailing-${index}`} />
                        ),
                    )}
                </TableRow>
            </TableHead>
            {items ? (
                <TableBody>
                    {items.map((item, index) => {
                        const itemKey = getItemKey(item);
                        return (
                            <React.Fragment key={itemKey}>
                                {renderRow({ item, itemKey, activeColumns, index })}
                            </React.Fragment>
                        );
                    })}
                </TableBody>
            ) : undefined}
        </Table>
    );
};

export default ResizableProfileTable;
